import { ContentLink, useXcoreQuery } from "@robe/client/xcore-ui";
import { Box, SimpleGrid } from "@xcorejs/ui";
import RobeLoader from "components/RobeLoader";
import ProductCard, { productCardProductFields } from "design-system/robe/Cards/Product";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { useLayout } from "xcore";
import { Product } from "xcore/types";
import ProductFilters from "./ProductFilters";

interface ProductFilterViewProps {
  filters: Record<string, string | string[]>;
}

const ProductFilterView = ({ filters }: ProductFilterViewProps) => {
  const { localize } = useLayout();
  const { locale } = useRouter();

  const { data: products, isLoading } = useXcoreQuery((core) =>
    core.content.multipleByType<Product>("product", {
      categories: resolveProductSubCategoriesIdsFromFilters(filters),
      fields: productCardProductFields,
      // todo xcc after localized images migrated remove workaround
      ...(locale === "ru" && {
        locales: ["ru", "en"],
      }),
    })
  );

  const validProducts = useMemo(
    () => products?.contents.filter((p) => !p.values.discontinuedGlob && !localize(p.values.discontinuedReg)),
    [localize, products]
  );

  return (
    <>
      <ProductFilters />

      {isLoading && <RobeLoader />}

      {validProducts && validProducts.length > 0 && (
        <Box mt="6rem">
          <SimpleGrid
            gap={{ _: "1.5rem", sm: "3rem" }}
            columns={{ _: 2, sm: 4 }}
            column="minmax(100px, 300px)"
            width={{ _: "auto", lg: "100%" }}
          >
            {validProducts.map((p) => (
              <ContentLink key={p.id} content={p} variant="simple" overflow="hidden" style={{ width: "100%", height: "100%" }}>
                <ProductCard product={p} />
              </ContentLink>
            ))}
          </SimpleGrid>
        </Box>
      )}
    </>
  );
};

export default ProductFilterView;

const resolveProductSubCategoriesIdsFromFilters = (filters: Record<string, string | string[]>) => {
  const seen = new Set<number>();

  return Object.values(filters).map((value) => {
    const numbers = Array.isArray(value) ? value.map(Number) : [Number(value)];
    return numbers.filter((n) => n !== 0 && !seen.has(n) && seen.add(n));
  });
};
